import { Product, Category, isPedixDomain } from '@pedix-workspace/utils';
import { Establishment } from '@pedix-workspace/utils';
import { Meta, MetaDefinition, Title } from '@angular/platform-browser';
import { Injectable, RendererFactory2, inject, computed, DOCUMENT, REQUEST } from '@angular/core';
import { isbot } from 'isbot';

import { EstablishmentUrlPipe } from '@pedix-workspace/pedixapp-shared-pipes';
import { getImageCdnUrl } from '@pedix-workspace/utils';
import { EnvironmentService } from '@pedix-workspace/angular-utils';
import { environment } from '../../../environments/environment';
import { AppRequestContextService } from '../../app-request-context.service';
import {
  buildLocalBusinessJsonLd,
  buildProductJsonLd,
  buildBreadcrumbJsonLd,
} from './seo-jsonld.utils';

type FaviconType = { src: string; sizes?: string; type?: string };

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  siteName = 'Pedix';
  currentMetaTags: HTMLMetaElement[] = [];
  private canonicalLink: HTMLLinkElement | null = null;
  private jsonLdScript: HTMLScriptElement | null = null;

  faviconsCreated = false;
  defaultFavicons: FaviconType[] = [
    { src: `/assets/icons/icon-48x48.png`, sizes: '48x48', type: 'image/png' },
    { src: `/assets/icons/icon-96x96.png`, sizes: '96x96', type: 'image/png' },
    { src: `/assets/icons/icon-144x144.png`, sizes: '144x144', type: 'image/png' },
  ];

  get defaultImage() {
    return `${environment.siteUrl}/assets/icons/icon-192x192.png`;
  }

  readonly establishment = computed(() => this.appRequestContextService.establishment());
  readonly displayName = computed(() => {
    const subsidiarySelector = this.appRequestContextService.subsidiarySelector();
    const parentCatalog = this.appRequestContextService.subsidiaryParentCatalog();

    if (subsidiarySelector && parentCatalog?.establishment?.name) {
      return parentCatalog.establishment.name;
    }
    return this.establishment()?.name || '';
  });

  private request = inject(REQUEST, { optional: true });

  private rendererFactory = inject(RendererFactory2);
  private document = inject(DOCUMENT);
  private title = inject(Title);
  private meta = inject(Meta);
  private appRequestContextService = inject(AppRequestContextService);
  private establishmentUrlPipe = inject(EstablishmentUrlPipe);
  private environmentService = inject(EnvironmentService);

  resetCurrentMetaTags() {
    this.currentMetaTags.forEach(metaTag => {
      this.meta.removeTagElement(metaTag);
    });
    this.currentMetaTags = [];

    if (this.canonicalLink) {
      this.canonicalLink.remove();
      this.canonicalLink = null;
    }

    if (this.jsonLdScript) {
      this.jsonLdScript.remove();
      this.jsonLdScript = null;
    }
  }

  setDefaultMetadata() {
    this.resetCurrentMetaTags();

    if (this.establishment()) {
      this.setEstablishmentMetadata(this.establishment());
    } else {
      this.setMetadata({ name: 'author', content: this.siteName });
      this.setMetadata({ name: 'description', content: 'Catálogo online y pedidos por Whatsapp.' });
      this.setMetadata({ property: 'og:title', content: this.siteName });
      this.setMetadata({
        property: 'og:description',
        content: 'Catálogo online y pedidos por Whatsapp.',
      });
      this.setMetadata({ property: 'og:site_name', content: this.siteName });
      this.setMetadata({ property: 'og:url', content: environment.siteUrl });
      this.setMetadata({ property: 'og:type', content: 'website' });
      this.setMetadata({ property: 'og:title', content: this.siteName });
      this.setMetadata({ property: 'og:image', itemprop: 'image', content: this.defaultImage });
      this.setMetadata({ property: 'og:image:alt', content: this.siteName });
      this.setMetadata({ property: 'twitter:card', content: 'summary_large_image' });
    }
  }

  setEstablishmentMetadata(
    establishment: Pick<
      Establishment,
      | 'name'
      | 'slug'
      | 'logo'
      | 'seoSocialMedia'
      | 'customDomain'
      | 'subdomainRedirection'
      | 'subdomainStatus'
      | 'phone'
      | 'addressDetails'
      | 'workingDays'
    >,
  ) {
    this.resetCurrentMetaTags();

    const displayName = this.displayName();

    this.title.setTitle(displayName);

    this.setMetadata({ name: 'author', content: this.siteName });
    this.setMetadata({ property: 'og:site_name', content: this.siteName });
    this.setMetadata({ property: 'twitter:card', content: 'summary_large_image' });
    this.setMetadata({ property: 'twitter:title', content: displayName });
    const establishmentUrl = this.establishmentUrlPipe.transform(establishment);

    this.setCanonicalUrl(establishmentUrl);
    this.setMetadata({ property: 'og:url', content: establishmentUrl });
    this.setMetadata({ property: 'og:type', content: 'website' });
    if (this.establishment().features.includes('WHITE_LABEL_BRANDING')) {
      this.setMetadata({ property: 'og:title', content: displayName });
    } else {
      this.setMetadata({
        property: 'og:title',
        content: `${this.siteName} | ${displayName}`,
      });
    }
    this.setMetadata({ property: 'og:image:alt', content: displayName });
    this.setMetadata({ property: 'twitter:image:alt', content: displayName });

    const isBot = isbot(this.request?.headers?.get?.('user-agent') || '');

    if (isBot && isPedixDomain(this.appRequestContextService.hostname())) {
      // NOTE: google search results allows only one icon per domain, so we use default favicons in those cases; for custom domains and/or subdomains that's ok
      this.setFavicons(this.defaultFavicons);
    } else if (establishment.seoSocialMedia?.favicon) {
      const faviconUrl = getImageCdnUrl(
        establishment.seoSocialMedia.favicon,
        {},
        this.environmentService.env,
      );
      this.setFavicons([{ src: faviconUrl }]);
    } else {
      this.setFavicons(this.defaultFavicons);
    }

    if (!establishment.seoSocialMedia) {
      const fallbackDescription = `${displayName} - Catálogo online y pedidos por WhatsApp`;
      this.setMetadata({ name: 'description', content: fallbackDescription });
      this.setMetadata({ property: 'og:description', content: fallbackDescription });
      this.setMetadata({ property: 'twitter:description', content: fallbackDescription });

      let logo = this.defaultImage;

      if (establishment.logo && this.establishment().features.includes('SEO_SOCIAL_MEDIA')) {
        logo = getImageCdnUrl(establishment.logo, { size: '250x250' }, this.environmentService.env);
      }

      this.setMetadata({ property: 'og:image', itemprop: 'image', content: logo });
    } else {
      const userAgent = this.request?.headers?.get?.('user-agent') || '';
      const isWhatsAppCrawler = userAgent.includes('WhatsApp/');

      const mediaImage = isWhatsAppCrawler
        ? establishment.seoSocialMedia?.shortImage
        : establishment.seoSocialMedia?.image;
      const socialMediaImage =
        getImageCdnUrl(
          mediaImage || establishment.logo,
          { size: '250x250' },
          this.environmentService.env,
        ) || this.defaultImage;
      const linkImage =
        getImageCdnUrl(
          mediaImage || establishment.logo,
          { size: '250x250' },
          this.environmentService.env,
        ) || this.defaultImage;
      const description =
        establishment.seoSocialMedia?.description || establishment.seoSocialMedia?.shortDescription;
      const shortDescription =
        establishment.seoSocialMedia?.shortDescription || establishment.seoSocialMedia?.description;

      if (description) {
        this.setMetadata({ name: 'description', content: description });
      }
      if (establishment.seoSocialMedia?.keywords) {
        this.setMetadata({
          name: 'keywords',
          content: establishment.seoSocialMedia.keywords.join(', '),
        });
      }

      if (shortDescription) {
        this.setMetadata({ property: 'twitter:description', content: shortDescription });
        this.setMetadata({ property: 'og:description', content: shortDescription });
      }
      if (socialMediaImage) {
        this.setMetadata({ property: 'twitter:image', content: socialMediaImage });
        this.setMetadata({ property: 'og:image', content: socialMediaImage });
        this.setMetadata({ property: 'og:image:url', content: socialMediaImage });
      }
      if (linkImage) {
        this.setMetadata({ property: 'og:image', itemprop: 'image', content: linkImage });
      }
    }

    const cdnLogoUrl = establishment.logo
      ? getImageCdnUrl(establishment.logo, { size: '250x250' }, this.environmentService.env)
      : null;

    const localBusinessJsonLd = buildLocalBusinessJsonLd(
      establishment,
      establishmentUrl,
      cdnLogoUrl,
    );
    const breadcrumbJsonLd = buildBreadcrumbJsonLd([{ name: displayName, url: establishmentUrl }]);
    this.setJsonLd({
      '@context': 'https://schema.org',
      '@graph': [localBusinessJsonLd, breadcrumbJsonLd],
    });
  }

  setCategoryMetadata(
    category: Pick<Category, 'id' | 'name' | 'image'>,
    establishment: Pick<
      Establishment,
      | 'name'
      | 'slug'
      | 'logo'
      | 'seoSocialMedia'
      | 'customDomain'
      | 'subdomainRedirection'
      | 'subdomainStatus'
    >,
  ) {
    if (this.appRequestContextService.activeCategories().length === 1) {
      this.setEstablishmentMetadata(this.establishment());
      return;
    }

    this.resetCurrentMetaTags();

    const displayName = this.displayName();
    const pageTitle = `${displayName} | ${category.name}`;

    this.title.setTitle(pageTitle);

    this.setMetadata({ name: 'author', content: this.siteName });
    this.setMetadata({ property: 'og:site_name', content: this.siteName });
    this.setMetadata({ property: 'twitter:card', content: 'summary_large_image' });
    this.setMetadata({ property: 'twitter:title', content: pageTitle });
    const categoryUrl = this.establishmentUrlPipe.transform(establishment, [
      'categoria',
      category.id,
    ]);
    const establishmentUrl = this.establishmentUrlPipe.transform(establishment);

    this.setCanonicalUrl(categoryUrl);
    this.setMetadata({ property: 'og:url', content: categoryUrl });
    this.setMetadata({ property: 'og:type', content: 'website' });

    if (this.establishment().features.includes('WHITE_LABEL_BRANDING')) {
      this.setMetadata({ property: 'og:title', content: pageTitle });
    } else {
      this.setMetadata({ property: 'og:title', content: `${this.siteName} | ${pageTitle}` });
    }
    this.setMetadata({ property: 'og:image:alt', content: category.name });
    this.setMetadata({ property: 'twitter:image:alt', content: category.name });

    const socialMediaImage =
      getImageCdnUrl(
        category.image || establishment.logo,
        {
          size: '1600x1600',
        },
        this.environmentService.env,
      ) || this.defaultImage;
    const linkImage =
      getImageCdnUrl(
        category.image || establishment.logo,
        { size: '250x250' },
        this.environmentService.env,
      ) || this.defaultImage;
    const description =
      establishment.seoSocialMedia?.description || establishment.seoSocialMedia?.shortDescription;
    const shortDescription =
      establishment.seoSocialMedia?.shortDescription || establishment.seoSocialMedia?.description;

    if (description) {
      this.setMetadata({ name: 'description', content: description });
    }
    if (shortDescription) {
      this.setMetadata({ property: 'twitter:description', content: shortDescription });
      this.setMetadata({ property: 'og:description', content: shortDescription });
    }
    if (establishment.seoSocialMedia?.keywords) {
      this.setMetadata({
        name: 'keywords',
        content: establishment.seoSocialMedia.keywords.join(', '),
      });
    }
    if (socialMediaImage) {
      this.setMetadata({ property: 'twitter:image', content: socialMediaImage });
      this.setMetadata({ property: 'og:image', content: socialMediaImage });
      this.setMetadata({ property: 'og:image:url', content: socialMediaImage });
    }
    if (linkImage) {
      this.setMetadata({ property: 'og:image', itemprop: 'image', content: linkImage });
    }

    const breadcrumbJsonLd = buildBreadcrumbJsonLd([
      { name: displayName, url: establishmentUrl },
      { name: category.name, url: categoryUrl },
    ]);
    this.setJsonLd({
      '@context': 'https://schema.org',
      '@graph': [breadcrumbJsonLd],
    });
  }

  setProductMetadata(
    product: Pick<
      Product,
      | 'id'
      | 'name'
      | 'description'
      | 'images'
      | 'price'
      | 'priceDiscount'
      | 'presentations'
    > & { active?: boolean },
    establishment: Pick<
      Establishment,
      | 'name'
      | 'slug'
      | 'logo'
      | 'seoSocialMedia'
      | 'customDomain'
      | 'subdomainRedirection'
      | 'subdomainStatus'
      | 'currencyCode'
    >,
    categoryId?: string,
  ) {
    this.resetCurrentMetaTags();

    const displayName = this.displayName();
    const pageTitle = `${displayName} | ${product.name}`;

    this.title.setTitle(pageTitle);

    this.setMetadata({ name: 'author', content: this.siteName });
    this.setMetadata({ property: 'og:site_name', content: this.siteName });
    this.setMetadata({ property: 'twitter:card', content: 'summary_large_image' });
    this.setMetadata({ property: 'twitter:title', content: pageTitle });
    const productPathParts = categoryId
      ? ['categoria', categoryId, 'producto', product.id]
      : ['producto', product.id];
    const productUrl = this.establishmentUrlPipe.transform(establishment, productPathParts);
    const establishmentUrl = this.establishmentUrlPipe.transform(establishment);

    this.setCanonicalUrl(productUrl);
    this.setMetadata({ property: 'og:url', content: productUrl });
    this.setMetadata({ property: 'og:type', content: 'website' });
    if (this.establishment().features.includes('WHITE_LABEL_BRANDING')) {
      this.setMetadata({ property: 'og:title', content: pageTitle });
    } else {
      this.setMetadata({ property: 'og:title', content: `${this.siteName} | ${pageTitle}` });
    }
    this.setMetadata({ property: 'og:image:alt', content: product.name });
    this.setMetadata({ property: 'twitter:image:alt', content: product.name });

    const socialMediaImage =
      getImageCdnUrl(
        product.images[0] || establishment.logo,
        {
          size: '1600x1600',
        },
        this.environmentService.env,
      ) || this.defaultImage;
    const linkImage =
      getImageCdnUrl(
        product.images[0] || establishment.logo,
        {
          size: '250x250',
        },
        this.environmentService.env,
      ) || this.defaultImage;

    if (product.description) {
      this.setMetadata({ name: 'description', content: product.description });
      this.setMetadata({ property: 'twitter:description', content: product.description });
      this.setMetadata({ property: 'og:description', content: product.description });
    }
    if (establishment.seoSocialMedia?.keywords) {
      this.setMetadata({
        name: 'keywords',
        content: establishment.seoSocialMedia.keywords.join(', '),
      });
    }
    if (socialMediaImage) {
      this.setMetadata({ property: 'twitter:image', content: socialMediaImage });
      this.setMetadata({ property: 'og:image', content: socialMediaImage });
      this.setMetadata({ property: 'og:image:url', content: socialMediaImage });
    }
    if (linkImage) {
      this.setMetadata({ property: 'og:image', itemprop: 'image', content: linkImage });
    }

    const cdnImageUrls = (product.images || [])
      .filter(img => !!img)
      .map(img => getImageCdnUrl(img, { size: '1600x1600' }, this.environmentService.env))
      .filter(url => !!url);

    if (cdnImageUrls.length === 0 && establishment.logo) {
      const logoUrl = getImageCdnUrl(
        establishment.logo,
        { size: '1600x1600' },
        this.environmentService.env,
      );
      if (logoUrl) {
        cdnImageUrls.push(logoUrl);
      }
    }

    const productJsonLd = buildProductJsonLd(product, establishment, productUrl, cdnImageUrls);
    const breadcrumbJsonLd = buildBreadcrumbJsonLd([
      { name: displayName, url: establishmentUrl },
      { name: product.name, url: productUrl },
    ]);
    this.setJsonLd({
      '@context': 'https://schema.org',
      '@graph': [productJsonLd, breadcrumbJsonLd],
    });
  }

  private setMetadata(metaDefinition: MetaDefinition) {
    const metaTag = this.meta.addTag(metaDefinition);

    this.currentMetaTags.push(metaTag);
  }

  private setCanonicalUrl(url: string) {
    if (this.canonicalLink) {
      this.canonicalLink.remove();
    }

    const renderer = this.rendererFactory.createRenderer(null, null);
    const link = renderer.createElement('link');

    renderer.setAttribute(link, 'rel', 'canonical');
    renderer.setAttribute(link, 'href', url);
    renderer.appendChild(this.document.head, link);

    this.canonicalLink = link;
  }

  private setJsonLd(data: Record<string, unknown>) {
    if (this.jsonLdScript) {
      this.jsonLdScript.remove();
      this.jsonLdScript = null;
    }

    const existing = this.document.head.querySelector('script[type="application/ld+json"]');
    if (existing) {
      existing.remove();
    }

    const renderer = this.rendererFactory.createRenderer(null, null);
    const script = renderer.createElement('script');

    renderer.setAttribute(script, 'type', 'application/ld+json');
    const json = JSON.stringify(data).replace(/</g, '\\u003c');
    renderer.appendChild(script, renderer.createText(json));
    renderer.appendChild(this.document.head, script);

    this.jsonLdScript = script;
  }

  private setFavicons(favicons: FaviconType[]) {
    if (this.faviconsCreated) {
      return;
    }
    const renderer = this.rendererFactory.createRenderer(null, null);

    favicons.forEach(favicon => {
      const link = renderer.createElement('link');

      renderer.setAttribute(link, 'rel', 'icon');
      renderer.setAttribute(link, 'href', favicon.src);

      if (favicon.sizes) {
        renderer.setAttribute(link, 'sizes', favicon.sizes);
      }
      if (favicon.type) {
        renderer.setAttribute(link, 'type', favicon.type);
      }

      renderer.appendChild(this.document.head, link);
    });

    this.faviconsCreated = true;
  }
}
